// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useStableCallback } from '@cloudscape-design/component-toolkit/internal';
export function useCellsResizeObserver(getCells, onResize) {
    const stableOnResize = useStableCallback(onResize);
    const observerRef = useRef(null);
    const observedCellsRef = useRef([]);
    // Cells can be replaced on any render, so the observed set is checked after each one.
    useLayoutEffect(() => {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const cells = getCells();
        const observedCells = observedCellsRef.current;
        if (haveSameElements(cells, observedCells)) {
            return;
        }
        if (!observerRef.current) {
            observerRef.current = new ResizeObserver(() => stableOnResize());
        }
        const observer = observerRef.current;
        observedCells.filter(cell => !cells.includes(cell)).forEach(cell => observer.unobserve(cell));
        cells.filter(cell => !observedCells.includes(cell)).forEach(cell => observer.observe(cell));
        observedCellsRef.current = cells;
    });
    useEffect(() => {
        return () => {
            var _a;
            (_a = observerRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            observerRef.current = null;
            observedCellsRef.current = [];
        };
    }, []);
}
function haveSameElements(a, b) {
    return a.length === b.length && a.every((element, index) => element === b[index]);
}
//# sourceMappingURL=use-cells-resize-observer.js.map